import { BaseCMI } from "../common/base_cmi";
import { CMIScore } from "../common/score";
import { Scorm12ValidationError } from "../../exceptions/scorm12_exceptions";
import { check12ValidFormat } from "../scorm12/validation";
import {
  scorm12_constants,
  scorm12_errors,
  scorm12_regex,
} from "../../constants";
import {
  addHHMMSSTimeStrings,
  getSecondsAsHHMMSS,
  getTimeAsSeconds,
} from "../../utilities";

/**
 * Class representing the `cmi.core` object
 * @extends BaseCMI
 */
export class CMICore extends BaseCMI {
  /**
   * Constructor for `cmi.core`
   */
  constructor() {
    super("cmi.core");
    this.score = new CMIScore({
      CMIElement: "cmi.core.score",
      score_children: scorm12_constants.score_children,
      score_range: scorm12_regex.score_range,
      invalidErrorCode: scorm12_errors.INVALID_SET_VALUE as number,
      invalidTypeCode: scorm12_errors.TYPE_MISMATCH as number,
      invalidRangeCode: scorm12_errors.VALUE_OUT_OF_RANGE as number,
      errorClass: Scorm12ValidationError,
    });
  }

  public readonly score: CMIScore;

  /**
   * Called when the API has been initialized after the CMI has been created
   */
  override initialize() {
    super.initialize();
    this.score?.initialize();
  }

  private readonly __children = scorm12_constants.core_children;
  private _student_id = "";
  private _student_name = "";
  private _lesson_location = "";
  private _credit = "";
  private _lesson_status = "not attempted";
  private _entry = "";
  private _total_time = "";
  private _lesson_mode = "normal";
  private _exit = "";
  private _session_time = "00:00:00";
  private _suspend_data = "";

  /**
   * Called when the API has been reset
   */
  reset(): void {
    this._initialized = false;

    this._exit = "";
    this._entry = "";

    /**
     * Resetting ensures we accurately track the time spent on each SCO session independently.
     * Each new session should start from zero, so we can reset cmi.core.session_time at the beginning of each session.
     */
    this._session_time = "00:00:00";

    this.score?.reset();
  }

  /**
   * Getter for __children
   * @return {string}
   * @private
   */
  get _children(): string {
    return this.__children;
  }

  /**
   * Setter for __children. Just throws an error.
   * @param {string} _children
   * @private
   */
  set _children(_children: string) {
    throw new Scorm12ValidationError(
      this._cmi_element + "._children",
      scorm12_errors.INVALID_SET_VALUE as number,
    );
  }

  /**
   * Getter for _student_id
   * @return {string}
   */
  get student_id(): string {
    return this._student_id;
  }

  /**
   * Setter for _student_id. Can only be called before  initialization.
   * @param {string} student_id
   */
  set student_id(student_id: string) {
    if (this.initialized) {
      throw new Scorm12ValidationError(
        this._cmi_element + ".student_id",
        scorm12_errors.READ_ONLY_ELEMENT as number,
      );
    } else {
      this._student_id = student_id;
    }
  }

  /**
   * Getter for _student_name
   * @return {string}
   */
  get student_name(): string {
    return this._student_name;
  }

  /**
   * Setter for _student_name. Can only be called before  initialization.
   * @param {string} student_name
   */
  set student_name(student_name: string) {
    if (this.initialized) {
      throw new Scorm12ValidationError(
        this._cmi_element + ".student_name",
        scorm12_errors.READ_ONLY_ELEMENT as number,
      );
    } else {
      this._student_name = student_name;
    }
  }

  /**
   * Getter for _lesson_location
   * @return {string}
   */
  get lesson_location(): string {
    return this._lesson_location;
  }

  /**
   * Setter for _lesson_location
   * @param {string} lesson_location
   */
  set lesson_location(lesson_location: string) {
    if (
      check12ValidFormat(
        this._cmi_element + ".lesson_location",
        lesson_location,
        scorm12_regex.CMIString256,
        true,
      )
    ) {
      this._lesson_location = lesson_location;
    }
  }

  /**
   * Getter for _credit
   * @return {string}
   */
  get credit(): string {
    return this._credit;
  }

  /**
   * Setter for _credit. Can only be called before  initialization.
   * @param {string} credit
   */
  set credit(credit: string) {
    if (this.initialized) {
      throw new Scorm12ValidationError(
        this._cmi_element + ".credit",
        scorm12_errors.READ_ONLY_ELEMENT as number,
      );
    } else {
      if (
        check12ValidFormat(this._cmi_element + ".credit", credit, scorm12_regex.CMICredit, true)
      ) {
        this._credit = credit;
      }
    }
  }

  /**
   * Getter for _lesson_status
   * @spec RTE 3.4.2.1.7 - cmi.core.lesson_status
   * @return {string}
   */
  get lesson_status(): string {
    return this._lesson_status;
  }

  /**
   * Setter for _lesson_status
   * @spec RTE 3.4.2.1.7 - cmi.core.lesson_status
   * @param {string} lesson_status
   */
  set lesson_status(lesson_status: string) {
    if (this.initialized) {
      if (
        check12ValidFormat(
          this._cmi_element + ".lesson_status",
          lesson_status,
          scorm12_regex.CMIStatus,
        )
      ) {
        this._lesson_status = lesson_status;
      }
    } else {
      if (
        check12ValidFormat(
          this._cmi_element + ".lesson_status",
          lesson_status,
          scorm12_regex.CMIStatus2,
        )
      ) {
        this._lesson_status = lesson_status;
      }
    }
  }

  /**
   * Getter for _entry
   * @return {string}
   */
  get entry(): string {
    return this._entry;
  }

  /**
   * Setter for _entry. Can only be called before  initialization.
   * @param {string} entry
   */
  set entry(entry: string) {
    if (this.initialized) {
      throw new Scorm12ValidationError(
        this._cmi_element + ".entry",
        scorm12_errors.READ_ONLY_ELEMENT as number,
      );
    } else {
      if (check12ValidFormat(this._cmi_element + ".entry", entry, scorm12_regex.CMIEntry, true)) {
        this._entry = entry;
      }
    }
  }

  /**
   * Getter for _total_time
   * @spec RTE 3.4.2.1.13 - cmi.core.total_time
   * @return {string}
   */
  get total_time(): string {
    return this._total_time;
  }

  /**
   * Setter for _total_time. Can only be called before  initialization.
   * @spec RTE 3.4.2.1.13 - cmi.core.total_time
   * @param {string} total_time
   */
  set total_time(total_time: string) {
    if (this.initialized) {
      throw new Scorm12ValidationError(
        this._cmi_element + ".total_time",
        scorm12_errors.READ_ONLY_ELEMENT as number,
      );
    } else {
      if (
        check12ValidFormat(
          this._cmi_element + ".total_time",
          total_time,
          scorm12_regex.CMITimespan,
          true,
        )
      ) {
        if (total_time) {
          const totalSeconds = getTimeAsSeconds(total_time, scorm12_regex.CMITimespan);
          this._total_time = getSecondsAsHHMMSS(totalSeconds);
        } else {
          this._total_time = total_time;
        }
      }
    }
  }

  /**
   * Getter for _lesson_mode
   * @return {string}
   */
  get lesson_mode(): string {
    return this._lesson_mode;
  }

  /**
   * Setter for _lesson_mode. Can only be called before  initialization.
   * @param {string} lesson_mode
   */
  set lesson_mode(lesson_mode: string) {
    if (this.initialized) {
      throw new Scorm12ValidationError(
        this._cmi_element + ".lesson_mode",
        scorm12_errors.READ_ONLY_ELEMENT as number,
      );
    } else {
      if (
        check12ValidFormat(this._cmi_element + ".lesson_mode", lesson_mode, scorm12_regex.CMILessonMode)
      ) {
        this._lesson_mode = lesson_mode;
      }
    }
  }

  /**
   * Getter for _exit. Should only be called during JSON export.
   * @return {string}
   */
  get exit(): string {
    if (!this.jsonString) {
      throw new Scorm12ValidationError(
        this._cmi_element + ".exit",
        scorm12_errors.WRITE_ONLY_ELEMENT as number,
      );
    }
    return this._exit;
  }

  /**
   * Setter for _exit
   *
   * @spec RTE 3.4.2.1.4 - cmi.core.exit
   *
   * SPEC COMPLIANCE NOTE:
   * The SCORM 1.2 specification defines exit vocabulary as: "time-out", "suspend", "logout", or ""
   * The value "normal" is NOT part of the SCORM 1.2 vocabulary (it's a SCORM 2004 value).
   *
   * This implementation accepts "normal" and normalizes it to "" (empty string) for the
   * following reasons:
   *
   * 1. Legacy content authored for SCORM 2004 sometimes runs in SCORM 1.2 mode
   * 2. Some authoring tools incorrectly use "normal" for SCORM 1.2 content
   * 3. Rejecting "normal" would break content with no user benefit
   * 4. Empty string ("") has the same semantic meaning as "normal" (regular exit)
   * 5. A console warning is logged to help developers identify the issue
   *
   * Strict spec vocabulary: "time-out" | "suspend" | "logout" | ""
   *
   * @param {string} exit
   */
  set exit(exit: string) {
    if (exit === "normal") {
      console.warn(
        "SCORM 1.2: Received non-standard value 'normal' for cmi.core.exit; normalizing to empty string.",
      );
      exit = "";
    }
    if (check12ValidFormat(this._cmi_element + ".exit", exit, scorm12_regex.CMIExit, true)) {
      this._exit = exit;
    }
  }

  /**
   * Getter for _session_time. Should only be called during JSON export.
   * @return {string}
   */
  get session_time(): string {
    if (!this.jsonString) {
      throw new Scorm12ValidationError(
        this._cmi_element + ".session_time",
        scorm12_errors.WRITE_ONLY_ELEMENT as number,
      );
    }
    return this._session_time;
  }

  /**
   * Setter for _session_time
   * @param {string} session_time
   */
  set session_time(session_time: string) {
    if (
      check12ValidFormat(
        this._cmi_element + ".session_time",
        session_time,
        scorm12_regex.CMITimespan,
      )
    ) {
      const totalSeconds = getTimeAsSeconds(session_time, scorm12_regex.CMITimespan);
      this._session_time = getSecondsAsHHMMSS(totalSeconds);
    }
  }

  /**
   * Getter for _suspend_data
   * @return {string}
   */
  get suspend_data(): string {
    return this._suspend_data;
  }

  /**
   * Setter for _suspend_data
   *
   * SPEC COMPLIANCE NOTE:
   * Uses CMIString64000 (64000 char limit) instead of spec-defined CMIString4096.
   * See scorm12_regex.CMIString64000 documentation for rationale.
   *
   * @param {string} suspend_data
   */
  set suspend_data(suspend_data: string) {
    if (
      check12ValidFormat(
        this._cmi_element + ".suspend_data",
        suspend_data,
        scorm12_regex.CMIString64000,
        true,
      )
    ) {
      this._suspend_data = suspend_data;
    }
  }

  /**
   * Adds the current session time to the existing total time.
   * @param {number} start_time
   * @return {string}
   */
  getCurrentTotalTime(start_time: number | undefined): string {
    let sessionTime = this._session_time;
    if (typeof start_time !== "undefined") {
      const seconds = new Date().getTime() - start_time;
      sessionTime = getSecondsAsHHMMSS(seconds / 1000);
    }

    return addHHMMSSTimeStrings(
      this._total_time,
      sessionTime,
      new RegExp(scorm12_regex.CMITimespan),
    );
  }

  /**
   * toJSON for cmi.core
   *
   * @return {
   *    {
   *      student_name: string,
   *      entry: string,
   *      exit: string,
   *      score: CMIScore,
   *      student_id: string,
   *      lesson_mode: string,
   *      lesson_location: string,
   *      lesson_status: string,
   *      credit: string,
   *      session_time: string
   *    }
   *  }
   */
  toJSON(): {
    student_name: string;
    entry: string;
    exit: string;
    score: CMIScore;
    student_id: string;
    lesson_mode: string;
    lesson_location: string;
    lesson_status: string;
    credit: string;
    session_time: string;
  } {
    this.jsonString = true;
    const result = {
      student_id: this.student_id,
      student_name: this.student_name,
      lesson_location: this.lesson_location,
      credit: this.credit,
      lesson_status: this.lesson_status,
      entry: this.entry,
      lesson_mode: this.lesson_mode,
      exit: this.exit,
      session_time: this.session_time,
      score: this.score,
    };
    this.jsonString = false;
    return result;
  }
}
